import { createApp, Component } from "vue"
import { useAppFlashes } from "@xy-planning-network/trees"
import GlobalComponents from "./plugins/global-components"
import User from "./types/user"
import "flatpickr/dist/flatpickr.min.css"
import "./tailwind.css"
import type { Integration } from "@sentry/types"
import * as Sentry from "@sentry/vue"
import { widget as supportWidget } from "@/composables/support"
import { useStripe } from "@/composables/useStripe"
import Env from "@/constants/environment"

// set the default email address for generic errors
useAppFlashes().configure({ email: "membership@xyplanningnetwork.com" })

const initVueApp = (component: Component): void => {
  const app = createApp(component, window.InitialVueProps)

  // Global component registration
  app.use(GlobalComponents)

  // Init Stripe SDK for fraud detection
  useStripe().init()

  // Sets up variables to manage sentry integration
  const currentUser: User | undefined =
    window.InitialVueProps?.initialProps?.currentUser

  // NOTE(dlk): we don't care to report errors crawlers may throw.
  const isSpider =
    // Baidu:
    // Mozilla/5.0 (iPhone; CPU iPhone OS 9_1 like Mac OS X) AppleWebKit/601.1.46 (KHTML, like Gecko) Version/9.0 Mobile/13B143 Safari/601.1 (compatible; Baiduspider-render/2.0; +http://www.baidu.com/search/spider.html)
    window.navigator.userAgent.includes("Baiduspider") ||
    // Bing:
    // Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; bingbot/2.0; +http://www.bing.com/bingbot.htm) Chrome/112.0.0.0 Safari/537.36
    window.navigator.userAgent.includes("bingbot") ||
    // Google:
    // Mozilla/5.0 AppleWebKit/537.36 (KHTML, like Gecko; compatible; Googlebot/2.1; +http://www.google.com/bot.html) Chrome/120.0.6099.71 Safari/537.36
    window.navigator.userAgent.includes("Googlebot")

  // Setup sentry error handling
  if (!Env.isDevelopment && !isSpider) {
    const integrations: Integration[] = [
      // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/filtering/#using-thirdpartyerrorfilterintegration
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ["xypn-portal-client"], // NOTE(spk): match applicationKey in vite.config.mts
        behaviour: "drop-error-if-exclusively-contains-third-party-frames",
      }),
    ]

    if (currentUser && Env.isProduction) {
      const allowUrls = []
      // TODO(dlk): remove once we debug the error thrown by some Uppy uploads.
      allowUrls.push("/api/v1/raw-data-uploads")
      integrations.push(
        Sentry.replayIntegration({
          mask: ["[data-private]", "input[type='password']"],
          maskAllText: false,
          maskAllInputs: false,
          networkDetailAllowUrls: allowUrls,
          networkRequestHeaders: ["X-Custom-Header"],
          networkResponseHeaders: ["X-Custom-Header"],
        })
      )
    }

    Sentry.init({
      allowUrls: [/^https?:\/\/(([A-Za-z0-9-]*)\.)?xyplanningnetwork\.com/],
      app: app,
      dsn: "https://8469518adeb84bd4994f81580df5978d@o320774.ingest.sentry.io/5507546",
      environment: Env.toString().toUpperCase(),
      replaysSessionSampleRate: 1.0,
      logErrors: true,
      trackComponents: true,
      integrations: integrations,
    })

    if (currentUser) {
      Sentry.setUser({ email: currentUser.email, id: currentUser.id })
    }
  }

  // init support widget with the current user
  supportWidget.identifyUser(currentUser)

  app.mount("#vue-app")
}

export default initVueApp
